import React, { useState, useMemo } from "react";
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    Input,
    Select,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Badge,
    useColorMode,
    useColorModeValue,
    Card,
    CardBody,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    InputGroup,
    InputLeftElement,
    Stat,
    StatLabel,
    StatNumber,
    SimpleGrid,
    Wrap,
    WrapItem,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor } from "../brandConfig";
import { usePageTitle } from "../hooks/useDocumentTitle";

// Data extracted from PERMISSIONS_MODULE_MATRIX.md
const modulesData = [
    { name: "Admin", permissions: ["ADMIN", "TENANT_MASTER_ADMIN"], description: "System dashboard, bill chaser automation" },
    { name: "Assets", permissions: ["ADMIN", "MANAGER", "ASSET_VIEWER", "ASSET_MANAGER", "EMPLOYEE"], description: "Asset tracking, QR codes, maintenance logging" },
    { name: "Bills", permissions: ["BILLS_ADMIN", "BILLS_USER", "ADMIN"], description: "Invoice creation, payment tracking, public bill viewing" },
    { name: "Calendars", permissions: ["CALENDAR_USER", "CALENDAR_VIEWER", "CALENDAR_MANAGER", "CALENDAR_ADMIN", "CALENDAR_ACCOUNTS_ADMIN"], description: "Calendar management, events, RSVPs, availability" },
    { name: "Clients", permissions: ["ADMIN", "MANAGER", "CLIENT_PERMISSIONS_MANAGEMENT"], description: "Client/customer management, permissions assignment" },
    { name: "Companies", permissions: ["ADMIN", "MANAGER", "COMPANY_VIEWER", "COMPANY_MANAGER"], description: "Organization/company records" },
    { name: "Company Knowledge", permissions: ["ADMIN", "COMPANY_KNOWLEDGE_AUTHOR", "COMPANY_KNOWLEDGE_EDITOR", "COMPANY_KNOWLEDGE_ADMIN"], description: "Internal knowledge base (any logged-in user can view)" },
    { name: "Domains", permissions: ["TENANT_USER", "TENANT_ADMIN"], description: "Domain management for tenants" },
    { name: "Emails", permissions: ["ADMIN", "MANAGER", "EMAIL_ADMIN", "EMAIL_USER", "EMAIL_INBOX_ADMIN", "ADDRESS_BOOK_ADMIN", "ADDRESS_BOOK_USER", "TENANT_MASTER_ADMIN"], description: "Email marketing, address book, inbox management" },
    { name: "Employees", permissions: ["ADMIN", "EMPLOYEE_VIEWER", "EMPLOYEE_MANAGER", "EMPLOYEE_ADMIN"], description: "Staff/workforce management" },
    { name: "Frontend Upgrades", permissions: ["FRONTEND_UPGRADES_ADMIN", "ADMIN"], description: "Track code improvements across tenant sites" },
    { name: "IPFS Videos", permissions: ["IPFS_ADMIN", "IPFS_USER"], description: "Decentralized video storage" },
    { name: "Knowledgebase", permissions: ["ADMIN", "TENANT_MASTER_ADMIN"], description: "Public knowledge base management" },
    { name: "Opportunities", permissions: ["OPPORTUNITIES_ADMIN", "ADMIN"], description: "Sales pipeline, deal tracking" },
    { name: "Passwords", permissions: ["ADMIN", "MANAGER", "PASSWORD_ADMIN", "PASSWORD_USER"], description: "Secure credential management" },
    { name: "Phone System", permissions: ["TENANT_ADMIN", "TENANT_USER", "PHONE_SYSTEM_USER", "PHONE_SYSTEM_ADMIN", "PHONE_SYSTEM_MASTER"], description: "VoIP phone system, call routing" },
    { name: "Products", permissions: ["ADMIN", "MANAGER", "USER"], description: "Product catalog, inventory" },
    { name: "Profile", permissions: ["USER", "ADMIN", "MANAGER"], description: "User profile management" },
    { name: "Projects", permissions: ["PROJECTS_ADMIN", "ADMIN", "MANAGER", "USER"], description: "Project management, tasks, timelines" },
    { name: "Proposals", permissions: ["PROPOSALS_VIEW", "PROPOSALS_CREATE", "PROPOSALS_ADMIN", "ADMIN"], description: "Business proposals, signatures" },
    { name: "Research & Design", permissions: ["ADMIN", "MANAGER", "USER"], description: "R&D tax incentive tracking" },
    { name: "Seller Profile", permissions: ["USER", "ADMIN", "MANAGER"], description: "Storefront for products" },
    { name: "Subscriptions", permissions: ["ADMIN", "MANAGER"], description: "Subscription management, analytics" },
    { name: "Tenant Management", permissions: ["ADMIN", "TENANT_MASTER_ADMIN"], description: "Multi-tenant administration" },
    { name: "Transcriptions", permissions: ["ADMIN", "TRANSCRIPTION_USER", "TRANSCRIPTION_ADMIN"], description: "Audio/video transcription service" },
    { name: "VAPI", permissions: ["TENANT_MASTER_ADMIN", "TENANT_ADMIN", "TENANT_USER", "VAPI_USER", "VAPI_ADMIN", "ADMIN"], description: "Voice AI assistant integration" },
    { name: "Video Server", permissions: ["ADMIN", "VIDEO_SERVER_USER", "VIDEO_SERVER_ADMIN"], description: "Professional video streaming" },
];

const permissionsData = [
    { name: "ADMIN", modules: "All modules", description: "Full system access - universal permission" },
    { name: "ADDRESS_BOOK_ADMIN", modules: "Emails", description: "Address book administration" },
    { name: "ADDRESS_BOOK_USER", modules: "Emails", description: "Address book user access" },
    { name: "ADMIN_DASHBOARD", modules: "Backend only", description: "Admin dashboard access" },
    { name: "ASSET_ADMIN", modules: "Assets (Backend)", description: "Full asset module access" },
    { name: "ASSET_MANAGER", modules: "Assets", description: "Create/edit assets, log maintenance" },
    { name: "ASSET_VIEWER", modules: "Assets", description: "View assets and scan QR codes" },
    { name: "BILLS_ADMIN", modules: "Bills, Tenant Management", description: "Bills module admin access" },
    { name: "BILLS_USER", modules: "Bills", description: "View and create bills" },
    { name: "CALENDAR_ACCOUNTS_ADMIN", modules: "Calendars", description: "Admin access to manage all calendars" },
    { name: "CALENDAR_ADMIN", modules: "Calendars", description: "Full calendar access across system" },
    { name: "CALENDAR_MANAGER", modules: "Calendars", description: "Create/edit calendars and events" },
    { name: "CALENDAR_USER", modules: "Calendars", description: "View and access own calendars" },
    { name: "CALENDAR_VIEWER", modules: "Calendars", description: "View calendars and events" },
    { name: "CLIENT", modules: "Backend only", description: "Client-specific routes" },
    { name: "CLIENT_PERMISSIONS_MANAGEMENT", modules: "Clients", description: "Manage client permissions" },
    { name: "COMPANY_ADMIN", modules: "Companies (Backend)", description: "Full company module access" },
    { name: "COMPANY_KNOWLEDGE_ADMIN", modules: "Company Knowledge", description: "Full knowledge base admin access" },
    { name: "COMPANY_KNOWLEDGE_AUTHOR", modules: "Company Knowledge", description: "Create/edit own articles" },
    { name: "COMPANY_KNOWLEDGE_EDITOR", modules: "Company Knowledge", description: "Edit any article" },
    { name: "COMPANY_MANAGER", modules: "Companies", description: "Create/edit companies" },
    { name: "COMPANY_VIEWER", modules: "Companies", description: "View company information" },
    { name: "EMAIL_ADMIN", modules: "Emails", description: "Email admin: accounts, inbox management" },
    { name: "EMAIL_INBOX_ADMIN", modules: "Emails", description: "Inbox management" },
    { name: "EMAIL_USER", modules: "Emails", description: "Email user: compose and send" },
    { name: "EMPLOYEE", modules: "Assets", description: "Indicates user is an employee (filtering)" },
    { name: "EMPLOYEE_ADMIN", modules: "Employees", description: "Full employee module access" },
    { name: "EMPLOYEE_MANAGER", modules: "Employees", description: "Create/edit/delete employees" },
    { name: "EMPLOYEE_VIEWER", modules: "Employees", description: "View employee records" },
    { name: "FRONTEND_UPGRADES_ADMIN", modules: "Frontend Upgrades", description: "Track frontend upgrades across tenants" },
    { name: "GUEST", modules: "Provider", description: "Guest access to public profiles" },
    { name: "IPFS_ADMIN", modules: "IPFS Videos", description: "Full IPFS admin access" },
    { name: "IPFS_USER", modules: "IPFS Videos", description: "Upload and view own IPFS videos" },
    { name: "MANAGER", modules: "Multiple modules", description: "Management-level access" },
    { name: "OPPORTUNITIES_ADMIN", modules: "Opportunities", description: "Opportunities module access" },
    { name: "PASSWORD_ADMIN", modules: "Passwords", description: "Password admin: manage all credentials" },
    { name: "PASSWORD_USER", modules: "Passwords", description: "Password user: view passwords" },
    { name: "PHONE_SYSTEM_ADMIN", modules: "Phone System", description: "Phone admin access" },
    { name: "PHONE_SYSTEM_MASTER", modules: "Phone System", description: "Phone master access" },
    { name: "PHONE_SYSTEM_USER", modules: "Phone System", description: "Phone system user access" },
    { name: "PRACTITIONER", modules: "Backend only", description: "Practitioner routes" },
    { name: "PROJECTS_ADMIN", modules: "Projects", description: "Projects module full access" },
    { name: "PROPOSALS_ADMIN", modules: "Proposals", description: "Full proposals admin access" },
    { name: "PROPOSALS_CREATE", modules: "Proposals", description: "Create proposals" },
    { name: "PROPOSALS_VIEW", modules: "Proposals", description: "View proposals" },
    { name: "SELLER", modules: "Backend only", description: "Seller routes" },
    { name: "SMS_CAMPAIGNS_ADMIN", modules: "Backend only", description: "SMS campaign admin" },
    { name: "SMS_CAMPAIGNS_USER", modules: "Backend only", description: "SMS campaign user" },
    { name: "TENANT_ADMIN", modules: "Phone System, VAPI, Domains", description: "Tenant admin access" },
    { name: "TENANT_MASTER_ADMIN", modules: "Admin, Emails, Tenant Management, VAPI, Knowledgebase", description: "Master tenant admin" },
    { name: "TENANT_USER", modules: "Phone System, VAPI, Domains", description: "Tenant user access" },
    { name: "TRANSCRIPTION_ADMIN", modules: "Transcriptions", description: "Transcription admin access" },
    { name: "TRANSCRIPTION_USER", modules: "Transcriptions", description: "View transcriptions" },
    { name: "USER", modules: "Products, Projects, Research & Design, Provider, Seller Profile, Profile", description: "Basic user access" },
    { name: "VAPI_ADMIN", modules: "VAPI", description: "VAPI admin access" },
    { name: "VAPI_USER", modules: "VAPI", description: "VAPI user access" },
    { name: "VIDEO_SERVER_ADMIN", modules: "Video Server", description: "Video server admin access" },
    { name: "VIDEO_SERVER_USER", modules: "Video Server", description: "Video server user access" },
    { name: "WEBSITE_MASTER", modules: "Backend only", description: "Website master access" },
];

const Permissions: React.FC = () => {
    const { colorMode } = useColorMode();
    const [searchQuery, setSearchQuery] = useState("");
    const [filterType, setFilterType] = useState<"all" | "modules" | "permissions">("all");

    usePageTitle("Permissions Matrix");

    const bgMain = getColor("background.main", colorMode);
    // Use proper dark mode card backgrounds
    const cardBg = useColorModeValue("white", "rgba(20, 20, 20, 0.9)");
    const cardBorder = useColorModeValue(getColor("border.light", "light"), "rgba(255, 255, 255, 0.1)");
    const textPrimary = useColorModeValue("#1A202C", "#FFFFFF");
    const textSecondary = useColorModeValue("#4A5568", "rgba(255, 255, 255, 0.7)");
    const inputBg = useColorModeValue("white", "gray.700");
    const tableBg = useColorModeValue("white", "rgba(30, 30, 35, 0.8)");
    const tableHeaderBg = useColorModeValue("gray.50", "rgba(40, 40, 45, 0.8)");

    // Get all unique permissions
    const allPermissions = useMemo(() => {
        const permissionSet = new Set<string>();
        modulesData.forEach(module => {
            module.permissions.forEach(perm => permissionSet.add(perm));
        });
        return Array.from(permissionSet).sort();
    }, []);

    // Filter modules based on search
    const filteredModules = useMemo(() => {
        return modulesData.filter(module =>
            module.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            module.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
            module.permissions.some(p => p.toLowerCase().includes(searchQuery.toLowerCase()))
        );
    }, [searchQuery]);

    // Filter permissions based on search
    const filteredPermissions = useMemo(() => {
        return permissionsData.filter(perm =>
            perm.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            perm.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
            perm.modules.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [searchQuery]);

    return (
        <Box bg={bgMain} minH="100vh">
            <NavbarWithCallToAction />

            <Container maxW="container.xl" py={8}>
                <VStack spacing={8} align="stretch">
                    {/* Header */}
                    <VStack spacing={2} align="start">
                        <Heading color={textPrimary}>Permissions & Module Matrix</Heading>
                        <Text color={textSecondary}>
                            Complete mapping of all 70 permissions across 30 modules
                        </Text>
                    </VStack>

                    {/* Stats */}
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                        <Card bg={cardBg} borderWidth="1px" borderColor={cardBorder}>
                            <CardBody>
                                <Stat>
                                    <StatLabel color={textSecondary}>Total Permissions</StatLabel>
                                    <StatNumber color={textPrimary}>70</StatNumber>
                                </Stat>
                            </CardBody>
                        </Card>
                        <Card bg={cardBg} borderWidth="1px" borderColor={cardBorder}>
                            <CardBody>
                                <Stat>
                                    <StatLabel color={textSecondary}>Frontend Modules</StatLabel>
                                    <StatNumber color={textPrimary}>30</StatNumber>
                                </Stat>
                            </CardBody>
                        </Card>
                        <Card bg={cardBg} borderWidth="1px" borderColor={cardBorder}>
                            <CardBody>
                                <Stat>
                                    <StatLabel color={textSecondary}>Backend Resolvers</StatLabel>
                                    <StatNumber color={textPrimary}>25+</StatNumber>
                                </Stat>
                            </CardBody>
                        </Card>
                    </SimpleGrid>

                    {/* Search and Filters */}
                    <Card bg={cardBg} borderWidth="1px" borderColor={cardBorder}>
                        <CardBody>
                            <HStack spacing={4}>
                                <InputGroup flex={1}>
                                    <InputLeftElement pointerEvents="none">
                                        <SearchIcon color={textSecondary} />
                                    </InputLeftElement>
                                    <Input
                                        placeholder="Search modules, permissions, or descriptions..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        bg={inputBg}
                                        color={textPrimary}
                                    />
                                </InputGroup>
                            </HStack>
                        </CardBody>
                    </Card>

                    {/* Tabs */}
                    <Tabs colorScheme="blue">
                        <TabList>
                            <Tab color={textPrimary}>Modules → Permissions</Tab>
                            <Tab color={textPrimary}>Permissions → Modules</Tab>
                        </TabList>

                        <TabPanels>
                            {/* Tab 1: Modules → Permissions */}
                            <TabPanel px={0}>
                                <Card bg={cardBg} borderWidth="1px" borderColor={cardBorder}>
                                    <CardBody p={0}>
                                        <Box overflowX="auto">
                                            <Table variant="simple" bg={tableBg}>
                                                <Thead bg={tableHeaderBg}>
                                                    <Tr>
                                                        <Th color={textPrimary} borderColor={cardBorder}>Module Name</Th>
                                                        <Th color={textPrimary} borderColor={cardBorder}>Permissions</Th>
                                                        <Th color={textPrimary} borderColor={cardBorder}>Description</Th>
                                                    </Tr>
                                                </Thead>
                                                <Tbody>
                                                    {filteredModules.map((module) => (
                                                        <Tr key={module.name}>
                                                            <Td color={textPrimary} fontWeight="semibold" borderColor={cardBorder}>
                                                                {module.name}
                                                            </Td>
                                                            <Td borderColor={cardBorder}>
                                                                <Wrap spacing={2}>
                                                                    {module.permissions.map(perm => (
                                                                        <WrapItem key={perm}>
                                                                            <Badge
                                                                                colorScheme={
                                                                                    perm === "ADMIN" ? "purple" :
                                                                                    perm.includes("ADMIN") ? "red" :
                                                                                    perm.includes("MANAGER") ? "orange" :
                                                                                    perm.includes("USER") ? "green" :
                                                                                    "blue"
                                                                                }
                                                                                fontSize="xs"
                                                                            >
                                                                                {perm}
                                                                            </Badge>
                                                                        </WrapItem>
                                                                    ))}
                                                                </Wrap>
                                                            </Td>
                                                            <Td color={textSecondary} fontSize="sm" borderColor={cardBorder}>
                                                                {module.description}
                                                            </Td>
                                                        </Tr>
                                                    ))}
                                                </Tbody>
                                            </Table>
                                        </Box>
                                    </CardBody>
                                </Card>
                            </TabPanel>

                            {/* Tab 2: Permissions → Modules */}
                            <TabPanel px={0}>
                                <Card bg={cardBg} borderWidth="1px" borderColor={cardBorder}>
                                    <CardBody p={0}>
                                        <Box overflowX="auto">
                                            <Table variant="simple" bg={tableBg}>
                                                <Thead bg={tableHeaderBg}>
                                                    <Tr>
                                                        <Th color={textPrimary} borderColor={cardBorder}>Permission</Th>
                                                        <Th color={textPrimary} borderColor={cardBorder}>Used In Modules</Th>
                                                        <Th color={textPrimary} borderColor={cardBorder}>Description</Th>
                                                    </Tr>
                                                </Thead>
                                                <Tbody>
                                                    {filteredPermissions.map((perm) => (
                                                        <Tr key={perm.name}>
                                                            <Td borderColor={cardBorder}>
                                                                <Badge
                                                                    colorScheme={
                                                                        perm.name === "ADMIN" ? "purple" :
                                                                        perm.name.includes("ADMIN") ? "red" :
                                                                        perm.name.includes("MANAGER") ? "orange" :
                                                                        perm.name.includes("USER") ? "green" :
                                                                        "blue"
                                                                    }
                                                                    fontSize="sm"
                                                                >
                                                                    {perm.name}
                                                                </Badge>
                                                            </Td>
                                                            <Td color={textPrimary} fontSize="sm" borderColor={cardBorder}>
                                                                {perm.modules}
                                                            </Td>
                                                            <Td color={textSecondary} fontSize="sm" borderColor={cardBorder}>
                                                                {perm.description}
                                                            </Td>
                                                        </Tr>
                                                    ))}
                                                </Tbody>
                                            </Table>
                                        </Box>
                                    </CardBody>
                                </Card>
                            </TabPanel>
                        </TabPanels>
                    </Tabs>

                    {/* Permission Hierarchy */}
                    <Card bg={cardBg} borderWidth="1px" borderColor={cardBorder}>
                        <CardBody>
                            <VStack align="start" spacing={4}>
                                <Heading size="md" color={textPrimary}>Permission Hierarchy</Heading>
                                <VStack align="start" spacing={2} pl={4}>
                                    <HStack>
                                        <Badge colorScheme="purple">ADMIN</Badge>
                                        <Text color={textSecondary}>Full system access (universal bypass)</Text>
                                    </HStack>
                                    <HStack>
                                        <Badge colorScheme="red">*_ADMIN</Badge>
                                        <Text color={textSecondary}>Module-specific admin access</Text>
                                    </HStack>
                                    <HStack>
                                        <Badge colorScheme="orange">MANAGER / *_MANAGER</Badge>
                                        <Text color={textSecondary}>Management-level access</Text>
                                    </HStack>
                                    <HStack>
                                        <Badge colorScheme="green">USER / *_USER</Badge>
                                        <Text color={textSecondary}>Standard user access</Text>
                                    </HStack>
                                    <HStack>
                                        <Badge colorScheme="blue">*_VIEWER</Badge>
                                        <Text color={textSecondary}>Read-only access</Text>
                                    </HStack>
                                    <HStack>
                                        <Badge colorScheme="gray">GUEST</Badge>
                                        <Text color={textSecondary}>Public/guest access</Text>
                                    </HStack>
                                </VStack>
                            </VStack>
                        </CardBody>
                    </Card>

                    {/* Notes */}
                    <Card bg={cardBg} borderWidth="1px" borderColor={cardBorder}>
                        <CardBody>
                            <VStack align="start" spacing={3}>
                                <Heading size="sm" color={textPrimary}>Important Notes</Heading>
                                <Text color={textSecondary} fontSize="sm">
                                    • <strong>ADMIN</strong> permission grants access to ALL modules (universal bypass)
                                </Text>
                                <Text color={textSecondary} fontSize="sm">
                                    • Some permissions are backend-only and don't appear in frontend moduleConfig
                                </Text>
                                <Text color={textSecondary} fontSize="sm">
                                    • Empty permissions array [] means the route is public/accessible to all users
                                </Text>
                                <Text color={textSecondary} fontSize="sm">
                                    • <strong>Company Knowledge</strong> module: Any logged-in user can VIEW articles (no permission required)
                                </Text>
                            </VStack>
                        </CardBody>
                    </Card>
                </VStack>
            </Container>

            <FooterWithFourColumns />
        </Box>
    );
};

export default Permissions;
